import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import { DomAttribute } from 'targus';
import { useArrowKeyIndex } from '../../../hooks/keyboard/useArrowKeyIndex';
import { useEnterKeyHandler } from '../../../hooks/keyboard/useEnterKeyHandler';
import { useHannibalQuery } from '../../../schema/hannibal/HannibalContext';
import { SuburbsByNameSearchDocument } from '../../../schema/hannibal/types.generated';
import { EMPTY_LIST } from '../../../util/EMPTY_LIST';
import { parseToEnum } from '../../../util/parseToEnum';
import { safeMemo } from '../../../util/safeMemo';
import { LoadingListItemGroup } from '../../LoadingListItem/LoadingListItemGroup/LoadingListItemGroup';
import { CountryState } from '../constants';
import { LocationChoice } from '../types/LocationChoice';
import { FilterTextInput } from './LocationSearch.styles';
import {
  LoadingWrapper,
  LocationList,
  LocationListContainer,
  LocationListPlaceholder,
} from './LocationSearch.styles';
import { LocationSearchItem } from './LocationSearchItem';

export interface Props {
  value: string;
  setValue: Setter<string>;
  onLocationSelected: Setter<LocationChoice>;
  onLocationTextChange: Setter<string>;
  setFocused?: Setter<boolean>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  locationFocused?: boolean;
  setShowPlaceholder: Setter<boolean>;
}

type Display = 'loading' | 'data' | 'error' | 'noResults';

export const LocationSearchBar = safeMemo(
  ({
    onLocationSelected,
    value: query,
    setValue: setQuery,
    onFocus,
    onBlur,
    onKeyDown,
    onLocationTextChange,
    locationFocused = false,
  }: Props) => {
    const ref = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      if (locationFocused) {
        ref.current?.focus();
      } else {
        ref.current?.blur();
      }
    }, [locationFocused]);

    const { data, loading, error } = useHannibalQuery(SuburbsByNameSearchDocument, {
      variables: { query },
      skip: !query,
    });

    const handleTextChange: DomAttribute<'input', 'onChange'> = e => {
      setQuery(e.currentTarget.value);
      onLocationTextChange(e.currentTarget.value);
    };

    const suburbs = data?.searchSuburbsByName.suburbs ?? EMPTY_LIST;

    const suburbChoices = useMemo(
      () => [
        ...suburbs.map(it => ({
          suburb: {
            name: it.name ?? undefined,
            state: parseToEnum(it.state, CountryState) ?? undefined,
            postcode: it.postcode ?? undefined,
          },
        })),
      ],
      [suburbs],
    );

    const choices: LocationChoice[] = suburbChoices;

    const [selected, setSelected] = useArrowKeyIndex(choices.length);

    const locationChoiceHandler = useCallback(
      (location?: LocationChoice) => {
        if (!locationFocused) {
          return;
        }

        const choice = location ?? choices[selected];

        if (choice) {
          setQuery(`${choice.suburb?.name}, ${choice.suburb?.state} ${choice.suburb?.postcode}`);

          onLocationSelected(choice);
        }
      },
      [choices, locationFocused, onLocationSelected, selected, setQuery],
    );

    useEnterKeyHandler(locationChoiceHandler);

    const display = getDisplay(choices, loading, error);

    const placeholder =
      display === 'data' ? 'Select a location' : 'Search by suburb, postcode or region.';

    return (
      <>
        <FilterTextInput
          onChange={handleTextChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          value={query}
          placeholder="Suburb, postcode, or region"
          ref={ref}
          data-link-id="Search bar location selection"
        />

        {display === 'loading' && locationFocused && (
          <LoadingWrapper>
            <LoadingListItemGroup />
          </LoadingWrapper>
        )}

        {(display === 'data' || display === 'noResults') && locationFocused && (
          <LocationListContainer>
            <LocationListPlaceholder>{placeholder}</LocationListPlaceholder>
            {display === 'data' && (
              <LocationList>
                {choices.map((choice, index) => (
                  <LocationSearchItem
                    key={index}
                    onChoose={locationChoiceHandler}
                    onSelect={setSelected}
                    choice={choice}
                    index={index}
                    selected={index === selected}
                  />
                ))}
              </LocationList>
            )}
          </LocationListContainer>
        )}
      </>
    );
  },
);

const getDisplay = (choices: LocationChoice[], loading: boolean, error: unknown): Display => {
  if (choices.length) return 'data';
  if (loading) return 'loading';
  return error ? 'error' : 'noResults';
};
